/**
 * 1.3.1 Administration → Media portal → Companies & users: a company's quota usage bars (Q1), this month's uploads
 * and the files waiting for review against quota_bytes_month / quota_bytes_pending, yellow from
 * PORTAL_QUOTA_WARNING_RATIO and red at the limit (GET /api/v1/admin/portal/usage). Rendered in the companies table's
 * Usage column (Companies.tsx). Q1's file.
 */
import { PORTAL_QUOTA_WARNING_RATIO, type PortalCompanyDTO, type PortalQuotaUsageDTO } from '@prism/contracts';
import { usePortalUsage } from '../../../api/hooks/portalAdmin';
import { bytes } from '../../../lib/format';

const levelOf = (ratio: number) => (ratio >= 1 ? 'full' : ratio >= PORTAL_QUOTA_WARNING_RATIO ? 'warn' : 'ok');

function Bar({ label, used, limit, ratio, testId }: { label: string; used: number; limit: number | null; ratio: number | null; testId: string }) {
  if (limit === null || ratio === null) {
    return (
      <div className="pq-row" data-testid={testId} data-level="none">
        <span className="pq-label">{label}</span>
        <span className="pq-text faint">{bytes(used)} · no quota</span>
      </div>
    );
  }
  const level = levelOf(ratio);
  const pct = Math.round(ratio * 100);
  return (
    <div className="pq-row" data-testid={testId} data-level={level} title={`${label}: ${bytes(used)} of ${bytes(limit)} (${pct} %)`}>
      <span className="pq-label">{label}</span>
      <span className="pq-bar" role="meter" aria-label={`${label} quota`} aria-valuemin={0} aria-valuemax={limit} aria-valuenow={Math.min(used, limit)} aria-valuetext={`${bytes(used)} of ${bytes(limit)}, ${pct} percent`}>
        <span className={`pq-fill ${level}`} style={{ width: `${Math.min(100, Math.max(ratio > 0 ? 2 : 0, pct))}%` }} />
      </span>
      <span className="pq-text">{bytes(used)} / {bytes(limit)}</span>
    </div>
  );
}

export function QuotaUsageBars({ company }: { company: PortalCompanyDTO }) {
  const usage = usePortalUsage();
  const u: PortalQuotaUsageDTO | undefined = usage.data?.items.find((x) => x.companyId === company.id);
  if (!u) return <span className="faint">{usage.isLoading ? '…' : '—'}</span>;
  return (
    <div className="pq" data-warning={u.warning || undefined}>
      <Bar label="This month" used={u.monthBytes} limit={u.quotaBytesMonth} ratio={u.monthRatio} testId="portal-usage-month" />
      <Bar label="Waiting" used={u.pendingBytes} limit={u.quotaBytesPending} ratio={u.pendingRatio} testId="portal-usage-pending" />
    </div>
  );
}
